// ============================================================
//  src/context/AuthContext.js
// ============================================================
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { login as apiLogin, logout as apiLogout, getDashboard } from '../api/client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [token, setToken]     = useState(() => localStorage.getItem('authToken'));
  const [loading, setLoading] = useState(true);

  // Persist token
  const saveToken = useCallback((t) => {
    setToken(t);
    if (t) localStorage.setItem('authToken', t);
    else   localStorage.removeItem('authToken');
  }, []);

  // Bootstrap: if token exists, fetch current user
  useEffect(() => {
    if (!token) { setLoading(false); return; }
    getDashboard()
      .then(({ data }) => {
        if (data.success) setUser(data.data.user);
        else saveToken(null);
      })
      .catch(() => saveToken(null))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const login = useCallback(async (identifier, password) => {
    const { data } = await apiLogin(identifier, password);
    if (data.success) {
      saveToken(data.data.token);
      setUser(data.data.user);
    }
    return data;
  }, [saveToken]);

  /**
   * loginWithToken — called after OTP email verification.
   * The verify-email API already issues a token + user payload,
   * so we don't need a separate login request.
   */
  const loginWithToken = useCallback((authToken, userData) => {
    saveToken(authToken);
    if (userData) setUser(userData);
  }, [saveToken]);

  const logoutUser = useCallback(async () => {
    try { await apiLogout(); } catch (_) {}
    saveToken(null);
    setUser(null);
  }, [saveToken]);

  const refreshUser = useCallback((updatedUser) => {
    setUser((prev) => ({ ...prev, ...updatedUser }));
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, loginWithToken, logout: logoutUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);